import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import styled from "styled-components";

import Icon from "components/atoms/Icon";
import Alert from "components/atoms/alert/Alert";
import Button from "components/atoms/button";
import Checkbox from "components/atoms/input/Checkbox";
import FormInput from "components/atoms/input/FormInput";
import RadioButton from "components/atoms/input/RadioButton";
import useLoading from "components/atoms/loading/useLoading";
import Text from "components/atoms/text/Text";
import useToast from "components/atoms/toast/useToast";

import AbstractModal from "components/page/modal/AbstractModal";
import CouponRegisterModal from "components/page/modal/CouponRegisterModal";

import {
    useGroupSubscriptionCreate,
    useGroupSubscriptionRollback,
    usePaymentBillingKeyCreate,
    usePaymentBillingKeyDelete,
    usePaymentCoupon,
    usePaymentDiscountPolicy,
    usePaymentPlan,
    useSpacePaymentHistoryCreate,
} from "hooks/queries/usePayment";

import Constants from "utils/constants";
import { createTypedError } from "utils/validation";

import moment from "moment";

const Container = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    min-height: 0;
`;

const SubInfoBox = styled.div`
    margin-top: 40px;
    padding: 8px 16px;
    width: 100%;
    background-color: var(--color-InfoBox);
    align-items: center;
    border-radius: 4px;
`;

const InfoListWrap = styled.ul`
    margin: 0;
    padding: 0 0 0 16px;
`;

const SubInfoItem = styled.li`
    margin: 4px 0;
    line-height: 1.4;
    text-align: justify;
    color: var(--color-SubBlack);
`;

const Wrapper = styled.div`
    margin: 20px 0 0 0px;
`;

const OptionWrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

const Option = styled.div`
    display: block;
    align-items: center;
    margin-bottom: 15px;
    padding: 16px;
    border-radius: 4px;
    cursor: pointer;
    border: ${(props) => (props.selected ? "1px solid var(--color-Button1)" : "1px solid #ccc")};

    &:last-child {
        margin-bottom: 0;
    }
`;

const HeaderRow = styled.div`
    display: flex;
    align-items: center;
`;

const Description = styled(Text)`
    font-size: 1.2rem;
    color: var(--color-SubBlack);
    margin-top: 10px;
`;

const OptionTitleText = styled(Text)`
    font-size: 1.4rem;
    font-weight: 500;
    margin: 40px 0 20px 0;
    padding: 0 0 0 8px;
    border-left: solid 2px var(--color-TitleBar);
`;

const contentStyle = {
    variant: "body-md-400",
    color: "var(--color-Black)",
};

const listStyle = {
    variant: "body-sm-400",
    textAlign: "justify",
};

const PlanInfoArea = styled.div`
    text-align: center;
`;
const PlanText = styled(Text)`
    font-size: 1.6rem;
    font-weight: 500;
    color: var(--color-Black);
`;

const PlanInfoText = styled(Text)`
    margin-top: 12px;
    font-size: 1.4rem;
    font-weight: 400;
    color: var(--color-SubBlack);
`;

const PlanSubText = styled(Text)`
    font-size: 1.2rem;
    font-weight: 400;
    color: var(--color-SubBlack);
    margin-top: 12px;
`;

const PaymentMethodText = styled(Text)`
    font-size: 1.4rem;
    color: var(--color-Black);
`;

const CouponRegisterText = styled(Text)`
    font-size: 1.2rem;
    font-weight: 400;
    margin-left: auto;
    color: var(--color-Grey1);
`;

const CouponList = styled.div`
    margin-top: 12px;
    display: flex;
    flex-direction: column;
`;

const CouponItem = styled.div`
    display: flex;
    align-items: flex-start;
    padding: 12px;
    background-color: var(--color-InfoBox);
    cursor: pointer;

    &:not(:first-child) {
        margin-top: 2px;
    }
`;

const CouponContent = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: 0px;
`;

const NoCouponText = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-SubBlack)",
})``;

const CardOptionWrapper = styled(Option)`
    flex-direction: column;
    align-items: flex-start;
    padding: 16px;
`;

const CardTermSelector = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 16px 0;
`;

const TermOption = styled.div`
    display: flex;
    align-items: center;
    padding: 8px 12px;
    border-radius: 4px;
    width: 100%;
    height: 36px;
    border: ${(p) => (p.selected ? "1px solid var(--color-Button1)" : "1px solid var(--color-Line)")};
    cursor: pointer;
`;

const DiscountBadge = styled(Text)`
    font-size: 1rem;
    font-weight: 500;
    color: white;
    background-color: var(--color-Button1);
    border-radius: 4px;
    padding: 2px 6px;
    margin-left: auto;
`;

const CardInfoBox = styled(SubInfoBox)`
    margin-top: 0;
    padding: 16px;
`;

const InfoRow = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const MemberRow = styled.div`
    display: flex;
    align-items: center;
    margin-top: 12px;
`;

const MemberVolumeText = styled(Text)`
    font-size: 1.4rem;
    font-weight: 400;
    color: var(--color-SubBlack);
    margin-left: 8px;
`;

const Divider = styled.hr`
    border: none;
    border-top: 1px solid var(--color-Line);
    margin: 12px 0;
`;

const ToggleRow = styled.div`
    display: flex;
    align-items: center;
    margin-top: 16px;
    width: 100%;
`;

const FeeSubText = styled(Text)`
    font-size: 1.2rem;
    font-weight: 400;
    color: var(--color-Grey1);
`;

function SpacePaymentNewModal(props) {
    const navigate = useNavigate();
    const [paymentMethod, setPaymentMethod] = useState("card");

    // 쿠폰
    const couponQuery = usePaymentCoupon();

    const [couponList, setCouponList] = useState([]);
    const [showCouponRegisterModal, setShowCouponRegisterModal] = useState(false);
    const [selectedCoupon, setSelectedCoupon] = useState(null);

    const [cardTerm, setCardTerm] = useState("1m"); // '1m' or '3m'

    const discountPolicyQuery = usePaymentDiscountPolicy();
    const [longTermDiscountPolicy, setLongTermDiscountPolicy] = useState({});
    const [groupDiscountPolicy, setGroupDiscountPolicy] = useState({});
    const [groupInfo, setGroupInfo] = useState({
        name: "",
        max_member: 2,
    });

    const paymentPlanQuery = usePaymentPlan();
    const [plan, setPlan] = useState({});

    // 자동 연장 여부
    const [autoExtend, setAutoExtend] = useState(true);

    const groupSubscriptionCreateMutation = useGroupSubscriptionCreate();
    const groupSubscriptionRollbackMutation = useGroupSubscriptionRollback();
    const billingKeyMutation = usePaymentBillingKeyCreate();
    const billingKeyDelete = usePaymentBillingKeyDelete();
    const spacePaymentHistoryCreateMutation = useSpacePaymentHistoryCreate();
    const { setLoading } = useLoading();
    const { setToast } = useToast();

    useEffect(() => {
        if (couponQuery.data) {
            if (!props.isGroupPlan) {
                const filteredCoupons = couponQuery.data.filter((coupon) => !coupon.used_at);
                setCouponList(filteredCoupons.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)));
            }
        }

        if (discountPolicyQuery.data) {
            if (props.isGroupPlan) {
                const groupPolicy = discountPolicyQuery.data.find(
                    (policy) => policy.internal_code === Constants.GROUP_DISCOUNT_POLICY,
                );
                setGroupDiscountPolicy(groupPolicy);
            }
            const longTermPolicy = discountPolicyQuery.data.find(
                (policy) => policy.internal_code === Constants.THREE_MONTH_DISCOUNT_POLICY,
            );
            setLongTermDiscountPolicy(longTermPolicy);
        }

        if (paymentPlanQuery.data) {
            if (props.isGroupPlan) {
                setPlan(paymentPlanQuery.data.find((plan) => plan.id === Constants.GROUP_PLAN));
            } else {
                setPlan(paymentPlanQuery.data.find((plan) => plan.id === Constants.PAID_PLAN));
            }
        }
    }, [couponQuery.data, discountPolicyQuery.data, paymentPlanQuery.data]);

    useEffect(() => {
        if (!props.isGroupPlan) {
            setPaymentMethod("card");
            if (couponList.length > 0) {
                setPaymentMethod("coupon");
                const latestCoupon = couponList.reduce((prev, current) =>
                    new Date(prev.updated_at) > new Date(current.updated_at) ? prev : current,
                );
                setSelectedCoupon(latestCoupon);
            } else if (props.isOnlyCouponUse) {
                setPaymentMethod("coupon");
            } else {
                setPaymentMethod("card");
            }
        }
    }, [couponList]);

    // region: 아임포트 결제
    useEffect(() => {
        const jquery = document.createElement("script");
        jquery.src = "https://code.jquery.com/jquery-1.12.4.min.js";
        const iamport = document.createElement("script");
        iamport.src = "https://cdn.iamport.kr/js/iamport.payment-1.1.8.js";
        document.head.appendChild(jquery);
        document.head.appendChild(iamport);

        return () => {
            document.head.removeChild(jquery);
            document.head.removeChild(iamport);
        };
    }, []);

    const handlePaymentError = (error) => {
        if (error?.cancelled) {
            return;
        }
        if (error?.type === "billingKey") {
            Alert("warn", "에러", "결제 정보 등록 중 오류가 발생했습니다. 결제 정보를 다시 확인해주세요.");
            return;
        }
        if (error?.type === "groupSpace") {
            Alert("warn", "에러", "그룹 스페이스 생성 중 오류가 발생했습니다. 다시 시도해주세요.");
            return;
        }
        if (error?.type === "paymentHistory") {
            Alert("warn", "에러", "구독 정보 변경 중 오류가 발생했습니다. 관리자에게 문의해주세요.");
            return;
        }
        if (error?.error_msg) {
            if (error.error_msg === "사용자가 결제를 취소하셨습니다") {
                return;
            }
            Alert("warn", "에러", "결제 중 오류가 발생했습니다. 결제 정보를 다시 확인해주세요.");
            return;
        }
        Alert("warn", "에러", error?.response?.data?.error || "요청 중 오류가 발생했습니다. 관리자에게 문의해주세요.");
    };

    const buildMerchantInfo = (email) => {
        const randomNumber = Math.floor(Math.random() * 90000) + 10000;
        const emailPrefix = (email || "user").split("@")[0];
        return {
            merchantUid: `new_${emailPrefix}_${randomNumber}`,
            customerUid: `${emailPrefix}_${randomNumber}`,
        };
    };

    const buildIamportPayload = ({ amount, merchantUid, customerUid }) => ({
        pg: `${process.env.REACT_APP_ENV !== "prd" ? "danal_tpay.9810030929" : "danal_tpay"}`,
        pay_method: "card",
        merchant_uid: merchantUid,
        name: `${plan.name} > ${cardTerm === "3m" ? "3개월" : "1개월"}`,
        period: {
            from: moment().format("YYYY-MM-DD"),
            to: moment()
                .add(cardTerm === "3m" ? 3 : 1, "months")
                .add(-1, "days")
                .format("YYYY-MM-DD"),
        },
        amount: amount,
        customer_uid: customerUid,
        buyer_email: props.user.email,
    });

    const requestIamportPayment = (payload) =>
        new Promise((resolve, reject) => {
            const { IMP } = window;
            IMP.init("imp65161342");
            document.getElementById("iamport-modal").style.display = "block";
            IMP.request_pay(payload, (response) => {
                document.getElementById("iamport-modal").style.display = "none";
                if (response.success) {
                    resolve(response);
                    return;
                }
                if (response.error_msg === "사용자가 결제를 취소하셨습니다") {
                    reject({ cancelled: true });
                    return;
                }
                reject(response);
            });
        });

    const rollbackGroupDraftIfNeeded = async (groupDraft) => {
        if (!groupDraft?.space_uuid || !groupDraft?.rollback_token) {
            return;
        }
        try {
            await groupSubscriptionRollbackMutation.mutateAsync({
                data: {
                    space_uuid: groupDraft.space_uuid,
                    rollback_token: groupDraft.rollback_token,
                },
            });
        } catch (rollbackError) {
            console.log("group rollback error", rollbackError);
        }
    };

    const getTargetSpaceUuid = (groupDraft) => {
        if (props.isGroupPlan) {
            return groupDraft?.space_uuid;
        }
        return props.user?.private_space?.uuid;
    };

    const payForSubscription = async () => {
        let groupDraft = null;
        let billingKey = null;
        try {
            const { merchantUid, customerUid } = buildMerchantInfo(props.user.email);
            const payload = buildIamportPayload({
                amount: paymentAmount,
                merchantUid,
                customerUid,
            });
            const response = await requestIamportPayment(payload);

            setLoading(true, "결제 요청을 처리 중입니다.");

            if (props.isGroupPlan) {
                try {
                    groupDraft = await groupSubscriptionCreateMutation.mutateAsync({
                        data: {
                            name: groupInfo.name.trim(),
                            max_member_count: groupInfo.max_member,
                        },
                    });
                } catch (error) {
                    throw createTypedError("groupSpace", error);
                }
            }

            const targetSpaceUuid = getTargetSpaceUuid(groupDraft);
            if (!targetSpaceUuid) {
                Alert("warn", "에러", "결제 대상 스페이스를 찾을 수 없습니다.");
                if (props.isGroupPlan) {
                    await rollbackGroupDraftIfNeeded(groupDraft);
                }
                return;
            }

            try {
                billingKey = await billingKeyMutation.mutateAsync(response);
            } catch (error) {
                throw createTypedError("billingKey", error);
            }

            const cardPaymentPayload = {
                started_at: payload.period.from,
                ended_at: payload.period.to,
                plan: plan.id,
                member_count: props.isGroupPlan ? groupInfo.max_member : 1,
                discount_policy: [
                    cardTerm === "3m" && longTermDiscountPolicy?.id,
                    props.isGroupPlan && groupDiscountPolicy?.id,
                ].filter(Boolean),
                amount: paymentAmount,
                status: Constants.PAYMENT_STATUS_COMPLETED,
                description: `${plan.name} 요금제 ${cardTerm === "3m" ? 3 : 1}개월 결제`,
                auto_extend: autoExtend,
                payment_type: Constants.PAYMENT_TYPE_DIRECT_NEW,
                card: billingKey.id,
                iamport_id: merchantUid,
                coupon: null,
                is_extend: false,
            };

            try {
                await spacePaymentHistoryCreateMutation.mutateAsync({
                    space_uuid: targetSpaceUuid,
                    data: cardPaymentPayload,
                });
            } catch (error) {
                throw createTypedError("paymentHistory", error);
            }

            if (!autoExtend) {
                billingKeyDelete.mutate(billingKey.id);
            }

            setToast("결제가 완료되었습니다.", "info");
            props.exitModal();
            navigate(`/${targetSpaceUuid}/management`);
        } catch (error) {
            if (billingKey?.id) {
                billingKeyDelete.mutate(billingKey.id);
            }
            if (props.isGroupPlan) {
                await rollbackGroupDraftIfNeeded(groupDraft);
            }
            handlePaymentError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleCouponPayment = async () => {
        if (selectedCoupon === null) {
            Alert("warn", "쿠폰 선택", "사용할 쿠폰을 선택해주세요.");
            return;
        }
        const targetSpaceUuid = getTargetSpaceUuid();
        if (!targetSpaceUuid) {
            Alert("warn", "에러", "결제 대상 스페이스를 찾을 수 없습니다.");
            return;
        }

        setLoading(true);
        try {
            const couponPaymentPayload = {
                started_at: null,
                ended_at: null,
                plan: plan.id,
                member_count: 1,
                discount_policy: [],
                amount: 0,
                status: Constants.PAYMENT_STATUS_COMPLETED,
                description: `${selectedCoupon.name} 쿠폰 사용`,
                auto_extend: false,
                payment_type: Constants.PAYMENT_TYPE_DIRECT_NEW,
                card: null,
                iamport_id: null,
                coupon: selectedCoupon.id,
                is_extend: false,
            };

            await spacePaymentHistoryCreateMutation.mutateAsync({
                space_uuid: targetSpaceUuid,
                data: couponPaymentPayload,
            });

            setToast("결제가 완료되었습니다.", "info");
            props.exitModal();
            navigate(`/${targetSpaceUuid}/management`);
        } catch (error) {
            Alert("warn", "에러", "쿠폰 사용 중 오류가 발생했습니다. 쿠폰 정보를 다시 확인해주세요.");
        } finally {
            setLoading(false);
        }
    };

    const planAmount = Number(plan?.price || 0);

    const monthlyAmount =
        planAmount -
        ((cardTerm === "3m" ? (planAmount * longTermDiscountPolicy.discount_rate) / 100 : 0) +
            (props.isGroupPlan ? (planAmount * groupDiscountPolicy.discount_rate) / 100 : 0));

    const paymentAmount = monthlyAmount * (cardTerm === "3m" ? 3 : 1) * (props.isGroupPlan ? groupInfo.max_member : 1);

    return (
        <AbstractModal
            modalTitle="요금제 결제"
            width={500}
            height={640}
            exitModal={props.exitModal}
            footer={{
                leftBtnText: "취소",
                rightBtnText: "결제하기",
                leftOnClick: props.exitModal,
                rightOnClick: () => {
                    if (paymentMethod === "coupon") {
                        handleCouponPayment();
                    } else {
                        if (props.isGroupPlan && groupInfo.name === "") {
                            Alert("warn", "그룹 정보", "그룹 이름을 입력해주세요.");
                            return;
                        }
                        payForSubscription();
                    }
                },
            }}
        >
            <Container>
                <PlanInfoArea>
                    <PlanText>{plan.name} 요금제</PlanText>
                    {props.isGroupPlan ? (
                        <>
                            <PlanInfoText>요금제 결제 후 스페이스가 추가됩니다.</PlanInfoText>
                            <PlanSubText>멤버당 20GB 용량 추가</PlanSubText>
                        </>
                    ) : (
                        <PlanSubText>20GB의 확장된 저장공간</PlanSubText>
                    )}
                    <PlanSubText>PDF 요약, 비평, 글쓰기 지원 도구 등 고급 연구 기능 사용</PlanSubText>
                </PlanInfoArea>
                {props.isGroupPlan && (
                    <>
                        <OptionTitleText>기본 정보</OptionTitleText>
                        <Wrapper>
                            <Text {...contentStyle}>스페이스 이름</Text>
                            <FormInput
                                margin="12px 0 12px 0"
                                width="100%"
                                labelDp="none"
                                placeholder={`${props.user.email}님의 연구그룹`}
                                type="text"
                                height="32px"
                                className="form-control"
                                name={"name"}
                                maxLength={255}
                                value={groupInfo.name}
                                onChange={(e) => setGroupInfo({ ...groupInfo, name: e.target.value })}
                            />
                            <Text {...contentStyle}>멤버 수</Text>
                            <MemberRow>
                                <Button
                                    onlyIcon
                                    bgColor={"var(--color-Button4)"}
                                    hoverBgColor={"var(--color-ButtonHover4)"}
                                    margin={"0 8px 0 0"}
                                    onClick={() => {
                                        setGroupInfo({
                                            ...groupInfo,
                                            max_member: Math.max(2, groupInfo.max_member - 1),
                                        });
                                    }}
                                >
                                    <Icon name={"minus"} size={"12"} color={"var(--color-Grey1)"} />
                                </Button>
                                <FormInput
                                    width="60px"
                                    height="32px"
                                    labelDp="none"
                                    type="number"
                                    className="form-control"
                                    name={"name"}
                                    textAlign={"center"}
                                    maxLength={255}
                                    value={groupInfo.max_member}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setGroupInfo({ ...groupInfo, max_member: parseInt(value, 10) });
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === "-" || e.key === "Subtract") {
                                            e.preventDefault();
                                        }
                                    }}
                                    onBlur={(e) => {
                                        const value = parseInt(e.target.value, 10);
                                        if (isNaN(value) || value < 2) {
                                            setGroupInfo({ ...groupInfo, max_member: 2 });
                                        } else {
                                            setGroupInfo({ ...groupInfo, max_member: value });
                                        }
                                    }}
                                />
                                <Button
                                    margin={"0 0 0 8px"}
                                    onlyIcon
                                    bgColor={"var(--color-Button4)"}
                                    hoverBgColor={"var(--color-ButtonHover4)"}
                                    onClick={() => {
                                        setGroupInfo({ ...groupInfo, max_member: groupInfo.max_member + 1 });
                                    }}
                                >
                                    <Icon name={"plus"} size={"12"} color={"var(--color-Grey1)"} />
                                </Button>
                                <MemberVolumeText>(사용 가능 용량: {20 * groupInfo.max_member} GB)</MemberVolumeText>
                            </MemberRow>
                        </Wrapper>
                    </>
                )}
                <OptionTitleText>결제 옵션</OptionTitleText>
                <Wrapper>
                    <OptionWrapper>
                        {!props.isGroupPlan && (
                            <Option selected={paymentMethod === "coupon"}>
                                <HeaderRow onClick={() => setPaymentMethod("coupon")}>
                                    <PaymentMethodText>쿠폰 결제</PaymentMethodText>
                                    <CouponRegisterText
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowCouponRegisterModal(true);
                                        }}
                                    >
                                        쿠폰 등록
                                    </CouponRegisterText>
                                </HeaderRow>
                                {paymentMethod === "coupon" && (
                                    <CouponList>
                                        {couponList.length === 0 ? (
                                            <NoCouponText>사용 가능한 쿠폰이 없습니다.</NoCouponText>
                                        ) : (
                                            couponList.map((coupon) => (
                                                <CouponItem
                                                    key={coupon.id}
                                                    selected={selectedCoupon?.number === coupon.number}
                                                    onClick={() => setSelectedCoupon(coupon)}
                                                >
                                                    <CouponContent>
                                                        <RadioButton
                                                            name="couponOption"
                                                            checked={selectedCoupon?.number === coupon.number}
                                                            onChange={() => setSelectedCoupon(coupon)}
                                                            label={coupon.name}
                                                            labelFontSize="1.4rem"
                                                        />
                                                        <Description>쿠폰 번호: {coupon.number}</Description>
                                                        <Description>
                                                            사용 기한: {moment(coupon.expired_at).format("YYYY.MM.DD")}
                                                        </Description>
                                                    </CouponContent>
                                                </CouponItem>
                                            ))
                                        )}
                                    </CouponList>
                                )}
                            </Option>
                        )}
                        {!props.isOnlyCouponUse && (
                            <CardOptionWrapper
                                selected={paymentMethod === "card"}
                                onClick={() => {
                                    setPaymentMethod("card");
                                }}
                            >
                                <HeaderRow>
                                    <PaymentMethodText>카드 결제</PaymentMethodText>
                                </HeaderRow>
                                {paymentMethod === "card" && (
                                    <>
                                        <CardTermSelector>
                                            <TermOption selected={cardTerm === "1m"} onClick={() => setCardTerm("1m")}>
                                                <RadioButton
                                                    name="cardTerm"
                                                    checked={cardTerm === "1m"}
                                                    onChange={() => setCardTerm("1m")}
                                                    label="1개월 이용"
                                                    labelFontSize="1.4rem"
                                                />
                                                {props.isGroupPlan && (
                                                    <DiscountBadge>
                                                        {Number(groupDiscountPolicy?.discount_rate)}% 할인
                                                    </DiscountBadge>
                                                )}
                                            </TermOption>

                                            <TermOption selected={cardTerm === "3m"} onClick={() => setCardTerm("3m")}>
                                                <RadioButton
                                                    name="cardTerm"
                                                    checked={cardTerm === "3m"}
                                                    onChange={() => setCardTerm("3m")}
                                                    label={`${longTermDiscountPolicy?.target_month}개월 이용`}
                                                    labelFontSize="1.4rem"
                                                />
                                                <DiscountBadge>
                                                    {Number(longTermDiscountPolicy?.discount_rate) +
                                                        (props.isGroupPlan
                                                            ? Number(groupDiscountPolicy?.discount_rate)
                                                            : 0)}
                                                    % 할인
                                                </DiscountBadge>
                                            </TermOption>
                                        </CardTermSelector>

                                        <CardInfoBox>
                                            <InfoRow>
                                                <Text {...contentStyle}>
                                                    월 이용 요금 {props.isGroupPlan && "(1명)"}
                                                </Text>
                                                <Text {...contentStyle}>{monthlyAmount.toLocaleString()}원</Text>
                                            </InfoRow>

                                            <InfoRow style={{ paddingLeft: "8px" }}>
                                                <FeeSubText>기본 요금</FeeSubText>
                                                <FeeSubText>{plan.price?.toLocaleString()}원</FeeSubText>
                                            </InfoRow>

                                            <InfoRow style={{ paddingLeft: "8px" }}>
                                                <FeeSubText>
                                                    장기 결제 할인 ({Number(longTermDiscountPolicy?.discount_rate)}
                                                    %)
                                                </FeeSubText>
                                                <FeeSubText>
                                                    {cardTerm === "3m" ? (
                                                        <>
                                                            -
                                                            {(
                                                                (plan?.price * longTermDiscountPolicy?.discount_rate) /
                                                                100
                                                            ).toLocaleString()}
                                                            원
                                                        </>
                                                    ) : (
                                                        "해당없음"
                                                    )}
                                                </FeeSubText>
                                            </InfoRow>

                                            {props.isGroupPlan && (
                                                <InfoRow style={{ paddingLeft: "8px" }}>
                                                    <FeeSubText>
                                                        그룹 결제 할인 ({Number(longTermDiscountPolicy?.discount_rate)}
                                                        %)
                                                    </FeeSubText>
                                                    <FeeSubText>
                                                        -
                                                        {(
                                                            (plan?.price * groupDiscountPolicy?.discount_rate) /
                                                            100
                                                        ).toLocaleString()}
                                                        원
                                                    </FeeSubText>
                                                </InfoRow>
                                            )}
                                            {props.isGroupPlan && (
                                                <InfoRow>
                                                    <Text {...contentStyle}>멤버 수</Text>
                                                    <Text {...contentStyle}>{groupInfo.max_member} 명</Text>
                                                </InfoRow>
                                            )}

                                            <InfoRow>
                                                <Text {...contentStyle}>이용 기간</Text>
                                                <Text {...contentStyle}>{cardTerm === "1m" ? "1개월" : "3개월"}</Text>
                                            </InfoRow>

                                            <Divider />

                                            <InfoRow>
                                                <Text {...contentStyle} fontWeight="bold">
                                                    결제 금액
                                                </Text>
                                                <Text {...contentStyle} fontWeight="bold">
                                                    {paymentAmount.toLocaleString()}원
                                                </Text>
                                            </InfoRow>
                                        </CardInfoBox>

                                        <ToggleRow>
                                            <Text {...contentStyle}>이용 기간 만료 후 자동 연장</Text>
                                            <Checkbox
                                                checked={autoExtend}
                                                onChange={(e) => {
                                                    setAutoExtend(e.target.checked);
                                                }}
                                                margin={"0 0 0 auto"}
                                            />
                                        </ToggleRow>
                                    </>
                                )}
                            </CardOptionWrapper>
                        )}
                    </OptionWrapper>
                </Wrapper>

                <SubInfoBox>
                    <InfoListWrap>
                        <SubInfoItem>
                            <Text {...listStyle}>표시된 모든 금액은 부가세(VAT)가 포함된 금액입니다.</Text>
                        </SubInfoItem>
                        <SubInfoItem>
                            <Text {...listStyle}>결제 완료 후 환불은 제공되지 않습니다.</Text>
                        </SubInfoItem>
                    </InfoListWrap>
                </SubInfoBox>
                {showCouponRegisterModal && (
                    <CouponRegisterModal exitModal={() => setShowCouponRegisterModal(!showCouponRegisterModal)} />
                )}
                <div
                    style={{
                        display: "none",
                        overFlow: "scroll",
                        position: "fixed",
                        zIndex: 10000,
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        padding: "120px 0",
                        background: "rgba(0,0,0,.48)",
                        boxSizing: "border-box",
                    }}
                    id={"iamport-modal"}
                ></div>
            </Container>
        </AbstractModal>
    );
}

export default SpacePaymentNewModal;
